import { useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import { useCurrentStudio } from '~entities/studios/model/studio-store';

/**
 * Returns a formatter that displays dates/times in the studio's configured
 * timezone (e.g. "America/Chicago") instead of the browser's local timezone.
 *
 * Falls back to browser-local formatting when the studio timezone is unavailable.
 */
export function useStudioDateFormatter() {
  const currentStudio = useCurrentStudio();
  const studioTimezone = currentStudio?.timezone;

  const formatInStudioTz = useCallback(
    (date: string | Date, fmt: string): string => {
      const iso = date instanceof Date ? date.toISOString() : date;
      try {
        if (studioTimezone) {
          return formatInTimeZone(iso, studioTimezone, fmt);
        }
        return format(typeof date === 'string' ? parseISO(date) : date, fmt);
      } catch {
        try {
          return format(typeof date === 'string' ? parseISO(date) : date, fmt);
        } catch {
          return '';
        }
      }
    },
    [studioTimezone],
  );

  return { formatInStudioTz, studioTimezone };
}
